export type BulkAssetLocation = {
  id?: string | null;
  name?: string | null;
};

export type BulkAssetLike = {
  id: string;
  name?: string | null;
  type?: string | null;
  deployment?: {
    location?: BulkAssetLocation | null;
    space?: { name?: string | null } | null;
  } | null;
};

export type BulkAssetTemplateLike = {
  id: string;
  name: string;
  quickCreate?: boolean | null;
  behavior?: unknown;
  tasks?: Array<{
    config?: {
      assetTypes?: string[] | null;
    } | null;
  }> | null;
};

export function getUniqueProperties(assets: BulkAssetLike[]) {
  const propertyMap = new Map<string, { id: string; name: string }>();
  const assetsWithoutDeployment: BulkAssetLike[] = [];

  assets.forEach((asset) => {
    const locationId = asset.deployment?.location?.id;
    const locationName = asset.deployment?.location?.name;

    if (locationId && locationName) {
      if (!propertyMap.has(locationId)) {
        propertyMap.set(locationId, { id: locationId, name: locationName });
      }
    } else {
      assetsWithoutDeployment.push(asset);
    }
  });

  return {
    properties: Array.from(propertyMap.values()),
    assetsWithoutDeployment,
  };
}

export function getAssetsWithDeployment(assets: BulkAssetLike[]) {
  return assets.filter((asset) => asset.deployment?.location?.id);
}

export function canCreateBulkAssetJob(
  propertiesCount: number,
  deployedCount: number
) {
  return propertiesCount === 1 && deployedCount > 0;
}

export function getJobCreationDisabledReason(
  propertiesCount: number,
  deployedCount: number
): string | null {
  if (propertiesCount > 1) {
    return 'You can only create a job for assets from one place at a time';
  }
  if (deployedCount === 0) {
    return 'Selected assets have no deployment location';
  }
  return null;
}

export function filterMatchingQuickCreateTemplates<
  T extends BulkAssetTemplateLike
>(templates: T[] | null | undefined, selectedAssetTypes: Set<string>): T[] {
  if (!templates) {
    return [];
  }

  const matched = templates.filter((template) => {
    if (!template.quickCreate) {
      return false;
    }

    if (!template.tasks || template.tasks.length === 0) {
      return true;
    }

    const templateAssetTypes = new Set<string>();
    template.tasks.forEach((task) => {
      if (task.config?.assetTypes) {
        task.config.assetTypes.forEach((type) => {
          templateAssetTypes.add(type);
        });
      }
    });

    if (templateAssetTypes.size === 0) {
      return true;
    }

    return Array.from(templateAssetTypes).some((type) =>
      selectedAssetTypes.has(type)
    );
  });

  const templateMap = new Map<string, T>();
  matched.forEach((template) => {
    if (!templateMap.has(template.id)) {
      templateMap.set(template.id, template);
    }
  });

  return Array.from(templateMap.values());
}
